import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { fetchOAuthClientInfo, postOAuthConsent, type OAuthClientInfoResponse } from '../../api/developers';
import { scopeLabel } from '../../lib/oauthScopes';
import { withReturnTo } from '../../lib/returnTo';
import Spinner from '../../components/Spinner';

const ERROR_MESSAGES: Record<string, string> = {
  invalid_client: 'client_id が正しくないか、この開発者アカウントは現在利用できません。',
  invalid_redirect_uri: 'redirect_uri がこのアプリに登録されているものと一致しません。',
  invalid_scope: '要求されている権限（scope）がこのアプリに許可されていません。',
  invalid_state: 'state が不正です。',
  invalid_challenge: 'code_challenge が不正です。',
  unauthorized_client: 'このクライアントはこの方式での認可を許可されていません。',
  unauthorized: 'セッションが切れました。お手数ですが再度ログインしてください。',
};

function describeError(code: string): string {
  return ERROR_MESSAGES[code] ?? `エラーが発生しました（${code}）`;
}

export default function Authorize() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const clientId = searchParams.get('client_id') ?? '';
  const redirectUri = searchParams.get('redirect_uri') ?? '';
  const scope = searchParams.get('scope') ?? '';
  const state = searchParams.get('state') ?? '';
  const codeChallenge = searchParams.get('code_challenge') ?? '';
  const codeChallengeMethod = searchParams.get('code_challenge_method') ?? '';
  const responseType = searchParams.get('response_type') ?? '';
  const unsupportedResponseType = responseType !== 'code';

  const [info, setInfo] = useState<OAuthClientInfoResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [busyDecision, setBusyDecision] = useState<'approve' | 'deny' | null>(null);

  // client 情報の取得は認証不要（未ログインでも同意画面の内容自体は確認できるようにする）。
  useEffect(() => {
    if (unsupportedResponseType) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    fetchOAuthClientInfo({ clientId, redirectUri, scope })
      .then((res) => {
        if (cancelled) return;
        if ('error' in res) setLoadError(res.error);
        else setInfo(res);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, redirectUri, scope, unsupportedResponseType]);

  // 未ログインなら、この同意画面に戻ってこられるよう現在の URL を携えてログイン画面へ。
  // state に加えクエリにも from を載せる（新規登録を挟む・リロードされる経路でも失われないよう）。
  useEffect(() => {
    if (ready && !user && !unsupportedResponseType) {
      const from = `${location.pathname}${location.search}`;
      navigate(withReturnTo('/login', from), {
        state: { from },
        replace: true,
      });
    }
  }, [ready, user, unsupportedResponseType, navigate, location]);

  const respond = async (decision: 'approve' | 'deny') => {
    setBusyDecision(decision);
    setSubmitError('');
    try {
      const res = await postOAuthConsent({
        clientId,
        redirectUri,
        scope,
        state,
        codeChallenge,
        codeChallengeMethod,
        decision,
      });
      if ('error' in res) {
        setSubmitError(res.error);
        setBusyDecision(null);
        return;
      }
      window.location.href = res.redirectTo;
    } catch (e) {
      setSubmitError(String(e));
      setBusyDecision(null);
    }
  };

  if (unsupportedResponseType) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1 className="auth-card__title">連携できません</h1>
          <div className="auth-form__error">サポートされていない response_type です。</div>
        </div>
      </div>
    );
  }

  if (!ready || loading || (ready && !user)) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <Spinner />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1 className="auth-card__title">連携できません</h1>
          <div className="auth-form__error">{describeError(loadError)}</div>
        </div>
      </div>
    );
  }

  if (!info) return null;

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1 className="auth-card__title">連携の確認</h1>
        <p className="auth-card__lead">
          <strong>{info.appName || `${info.ownerName} のアプリ`}</strong> が VRMA Hub のあなたのアカウントに
          次の権限でアクセスしようとしています。
        </p>

        {info.appDescription && <p>{info.appDescription}</p>}
        <p style={{ fontSize: 13, opacity: 0.8 }}>開発者: {info.ownerName}</p>

        <p style={{ marginTop: 16, marginBottom: 4, fontWeight: 700 }}>要求されている権限</p>
        {info.scopes.length === 0 ? (
          <p>権限の要求はありません。</p>
        ) : (
          <ul>
            {info.scopes.map((s) => (
              <li key={s}>{scopeLabel(s)}</li>
            ))}
          </ul>
        )}

        <p style={{ marginTop: 16, marginBottom: 4, fontWeight: 700 }}>redirect_uri</p>
        <pre className="code-block">
          <code>{info.redirectUri}</code>
        </pre>

        {submitError && (
          <div className="auth-form__error" style={{ marginTop: 12 }}>
            {describeError(submitError)}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
          <button
            type="button"
            className="btn"
            onClick={() => respond('deny')}
            disabled={busyDecision !== null}
          >
            {busyDecision === 'deny' ? '処理中…' : '拒否する'}
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => respond('approve')}
            disabled={busyDecision !== null}
          >
            {busyDecision === 'approve' ? '処理中…' : '許可する'}
          </button>
        </div>
      </div>
    </div>
  );
}
